import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { pb } from '../api/pocketbase';
import { currentSession, signInWithPassword, signOut as pbSignOut, type Session } from '../api/auth';

export type { Session } from '../api/auth';

type SessionContextValue = {
  session: Session | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(currentSession);

  useEffect(() => pb.authStore.onChange(() => setSession(currentSession())), []);

  // Auth responses expand the tenant; a reload may not, so resolve the display name once.
  useEffect(() => {
    if (!session || session.tenant.name !== session.tenant.id) return;
    let cancelled = false;
    void pb
      .collection('tenants')
      .getOne(session.tenant.id)
      .then((tenant) => {
        if (cancelled) return;
        setSession((prev) =>
          prev ? { ...prev, tenant: { ...prev.tenant, name: String(tenant.name ?? prev.tenant.name) } } : prev
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      signIn: async (email, password) => {
        const next = await signInWithPassword(email, password);
        setSession(next);
      },
      signOut: () => {
        pbSignOut();
        setSession(null);
      }
    }),
    [session]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within a SessionProvider');
  return context;
}
